import { useState, useEffect, useRef } from 'react';
import Modal from './Modal';
import { commentsApi, paymentsApi, formatCurrency } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import Icon from './Icon';
import { toast } from './Toast';
import { PaymentMethodIcon } from '../pages/SupplierManagementPage';
import { formatUploadedFull } from '../utils/dateFormat';

// A document's notes arrive from TWO places, and until now this modal only ever
// showed one of them:
//   • הוסף הערה  → the `comments` table. A conversation; any user, any time.
//   • בוצע תשלום → `payments.notes`. Written to explain an amount that differs
//                  from the document, and MANDATORY in that case
//                  (server/lib/paymentAmount.js).
// Both are "a comment on this document" to the person reading them, so hiding
// the second here meant opening צפה בהערות on a part-paid invoice and seeing no
// trace of why it was part-paid. They are merged into one timeline, but a
// payment-born note is badged with the amount and method it explains — the
// context that makes it readable at all ("₪2,000 of ₪3,200, bank transfer").
//
// Payments with no note are deliberately left out: this is the comments view,
// and a payment nobody wrote anything about is not a comment. The full payment
// list lives in PaymentDetailPanel, opened from the row's שולם badge.
// `initialText` pre-fills the box (e.g. the missing-notes report from
// LinkDeliveryNotesModal); the user still reviews, picks recipients and saves.
// `internalOnlyReason`, when set, withholds the customer email picker and
// shows the reason instead — a draft is invisible to customers, so emailing
// them about it points at a document they cannot open.
// The one wording for "why no customer email here", shared by every surface
// that opens a draft's thread.
export const DRAFT_INTERNAL_ONLY_REASON = 'הלקוח לא רואה טיוטות — אפשר לשלוח לו אחרי אישור המסמך';

// `onSendAfterApproval(text)`, when given, adds a button that hands the text
// back instead of saving it: the page re-opens it, with the picker, once the
// draft is approved (same shape as the מרכזת missing-list flow).
// `loadDocComments()`, when given (the /suppliers list), also merges in the
// notes written on the supplier's documents in the row's window — each badged
// with the document it is on, since it was not written on this thread.
// `periodMonth` ('YYYY-MM'), when given, is saved with a new supplier note: the
// month it is about, which is where the list's הערות cell will count it.
// `money` formats payment entries: two decimals by default, whole shekels when
// opened from the payment screens (/suppliers, /suppliers/paid).
export default function CommentsModal({ type, referenceId, title, onClose, onCommentAdded, initialText = '', internalOnlyReason = '', onSendAfterApproval, loadDocComments, periodMonth, money = formatCurrency }) {
  const { user, canEdit } = useAuth();
  const [comments,   setComments]   = useState([]);
  const [payNotes,   setPayNotes]   = useState([]);
  const [docNotes,   setDocNotes]   = useState([]);
  const [text,       setText]       = useState(initialText);
  const [saving,     setSaving]     = useState(false);
  const [recipients, setRecipients] = useState([]);   // [{id, email}] customers of this restaurant
  const [notifyIds,  setNotifyIds]  = useState([]);   // selected recipient ids
  const textRef = useRef(null);

  useEffect(() => {
    commentsApi.getAll(type, referenceId).then(r => setComments(r.data)).catch(() => {});
    // Internal-only threads never render the picker, so don't fetch it.
    if (!internalOnlyReason) {
      commentsApi.recipients(type, referenceId).then(r => setRecipients(r.data)).catch(() => {});
    }
    // Documents only — a supplier-level thread has no single payment to explain.
    if (type === 'document') {
      paymentsApi.getByDocument(referenceId)
        .then(r => setPayNotes((r.data || []).filter(p => (p.notes || '').trim())))
        .catch(() => {});   // a failed fetch must not blank the conversation
    }
    if (loadDocComments) {
      loadDocComments().then(r => setDocNotes(r.data || [])).catch(() => {});
    }
    setTimeout(() => textRef.current?.focus(), 80);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleNotify = (id) =>
    setNotifyIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSaving(true);
    try {
      await commentsApi.create({
        type, reference_id: referenceId, comment: text.trim(),
        notify: internalOnlyReason ? [] : notifyIds,
        ...(periodMonth ? { period_month: periodMonth } : {}),
      });
    } catch (err) {
      // Nothing was saved: keep the text and say so, instead of a window that
      // silently stays put and reads as stuck.
      toast(err.response?.data?.error || 'שגיאה בשמירת ההערה', 'error');
      setSaving(false);
      return;
    }
    // Saved (and emailed). From here on a failure must not look like the save
    // failed, or the user sends it again and the customer gets it twice — so
    // the refresh is best-effort and onCommentAdded fires regardless.
    const saved = text.trim();
    setText('');
    setNotifyIds([]);
    try {
      const r = await commentsApi.getAll(type, referenceId);
      setComments(r.data);
    } catch { /* the thread shows it on next open */ }
    setSaving(false);
    onCommentAdded?.(saved);
    textRef.current?.focus();
  };

  // One timeline, newest first. `kind` is what the renderer branches on, so the
  // two origins can never be styled the same by accident.
  const entries = [
    ...comments.map(c => ({
      kind: 'comment', id: `c${c.id}`, at: c.created_at,
      who: c.username, text: c.comment,
      period: c.period_month,
    })),
    ...payNotes.map(p => ({
      kind: 'payment', id: `p${p.id}`, at: p.created_at,
      who: p.recorded_by_label, text: p.notes,
      // NULL amount means "the document's full total" (lib/paymentAmount.js).
      amount: p.amount != null ? p.amount : p.doc_total,
      method: p.payment_method,
      docTotal: p.doc_total,
    })),
    ...docNotes.map(c => ({
      kind: 'doc', id: `d${c.id}`, at: c.created_at,
      who: c.username, text: c.comment,
      docLabel: [c.doc_type, c.doc_num].filter(Boolean).join(' '),
      docDate: c.doc_date,
    })),
  ].sort((a, b) => String(b.at || '').localeCompare(String(a.at || '')));

  return (
    <Modal onClose={onClose} title={`הערות — ${title}`} size={520} closeOnBackdrop={false}>

    <div style={{ maxHeight: 280, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
      {entries.length === 0 ? (
        <p style={{ color: '#bbb', textAlign: 'center', fontSize: '0.88rem', padding: '20px 0' }}>אין הערות עדיין</p>
      ) : entries.map(e => {
        const fromPayment = e.kind === 'payment';
        return (
          <div key={e.id} style={{
            background: fromPayment ? '#fffbeb' : '#f7f9fc',
            border: fromPayment ? '1px solid #fde68a' : '1px solid transparent',
            borderRadius: 10, padding: '10px 14px',
          }}>
            {/* The origin line. It leads the card rather than trailing it,
                because "this is about a payment" is what reframes the text
                below — read afterwards it is too late to be context. */}
            {fromPayment && (
              <div style={{
                display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8,
                fontSize: '0.78rem', fontWeight: 700, color: '#b45309', marginBottom: 6,
              }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Icon name="card" size={13} /> בוצע תשלום
                </span>
                <span style={{ color: '#92400e' }}>
                  {money(e.amount)}
                  {e.docTotal != null && ` מתוך ${money(Math.abs(e.docTotal))}`}
                </span>
                {e.method && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#92400e', fontWeight: 600 }}>
                    <PaymentMethodIcon method={e.method} size={13} /> {e.method}
                  </span>
                )}
              </div>
            )}
            {e.period && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', fontWeight: 700, color: 'var(--action)', marginBottom: 6 }}>
                <Icon name="calendar" size={13} /> על חודש {e.period.slice(5, 7)}/{e.period.slice(0, 4)}
              </div>
            )}
            {e.kind === 'doc' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', fontWeight: 700, color: 'var(--action)', marginBottom: 6 }}>
                <Icon name="comment" size={13} /> על {e.docLabel || 'מסמך'}{e.docDate ? ` · ${e.docDate}` : ''}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
              <span style={{ fontWeight: 700, fontSize: '0.82rem', color: fromPayment ? '#92400e' : '#0f3460' }}>{e.who}</span>
              <span style={{ fontSize: '0.75rem', color: '#bbb', whiteSpace: 'nowrap' }}>{formatUploadedFull(e.at)}</span>
            </div>
            <div style={{ fontSize: '0.88rem', color: '#333', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{e.text}</div>
          </div>
        );
      })}
    </div>

    {!canEdit ? (
      // Viewers are read-only: they can read the thread but not post.
      <div className="modal-actions" style={{ borderTop: '1px solid #eee', paddingTop: 16 }}>
        <button type="button" className="btn btn-secondary" onClick={onClose}>סגור</button>
      </div>
    ) : (
    <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 10, borderTop: '1px solid #eee', paddingTop: 16 }}>
      {user?.email && (
        <div style={{ fontSize: '0.8rem', color: 'var(--ink-muted)', marginBottom: 2 }}>
          מגיב בתור: <strong style={{ color: '#0f3460' }}>{user.email}</strong>
        </div>
      )}
      <textarea
        ref={textRef}
        className="form-control"
        placeholder="כתוב הערה... (Ctrl+Enter לשליחה)"
        value={text}
        onChange={e => setText(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) submit(e); }}
        rows={initialText ? 6 : 3}
        style={{ resize: 'vertical', fontFamily: 'inherit' }}
      />
      {internalOnlyReason ? (
        <div style={{ fontSize: '0.8rem', color: '#777' }}>{internalOnlyReason}</div>
      ) : recipients.length > 0 && (
        <div style={{ fontSize: '0.82rem', color: 'var(--ink-muted)' }}>
          <div style={{ marginBottom: 6, fontWeight: 600, color: '#0f3460', display: 'flex', alignItems: 'center', gap: 6 }}><Icon name="mail-send" size={15} /> שלח גם ללקוח במייל:</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {recipients.map(r => (
              <label key={r.id} style={{
                display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                background: notifyIds.includes(r.id) ? '#e7f0fb' : '#f7f9fc',
                border: `1px solid ${notifyIds.includes(r.id) ? '#0f3460' : '#e3e8ef'}`,
                borderRadius: 8, padding: '5px 10px',
              }}>
                <input
                  type="checkbox"
                  checked={notifyIds.includes(r.id)}
                  onChange={() => toggleNotify(r.id)}
                  style={{ margin: 0 }}
                />
                {r.email}
              </label>
            ))}
          </div>
        </div>
      )}
      <div className="modal-actions">
        <button type="button" className="btn btn-secondary" onClick={onClose}>סגור</button>
        {onSendAfterApproval && (
          <button
            type="button"
            className="btn btn-outline"
            disabled={!text.trim() || saving}
            onClick={() => onSendAfterApproval(text.trim())}
          >
            <Icon name="mail-send" size={15} /> שלח ללקוח אחרי האישור
          </button>
        )}
        <button type="submit" className="btn btn-primary" disabled={saving || !text.trim()}>
          {saving ? 'שומר...' : (notifyIds.length ? `שמור ושלח ל-${notifyIds.length}` : 'שמור הערה')}
        </button>
      </div>
    </form>
    )}
    </Modal>
  );
}
